/**
 * Report Constants
 * Constants used specifically in Reports/Analytics pages
 */

import { STATUS_COLORS } from './statusColors'

/**
 * Filter Values
 */
export const FILTER_VALUES = {
  UNASSIGNED: 'unassigned'
}

/**
 * Estimation Range Labels (in minutes)
 */
export const ESTIMATION_RANGE_LABELS = {
  TEST_CASES: {
    RANGE_0_15: '0-15 min',
    RANGE_16_30: '16-30 min',
    RANGE_31_60: '31-60 min',
    RANGE_61_120: '61-120 min',
    RANGE_121_PLUS: '121+ min',
    NOT_ESTIMATED: 'Not estimated'
  },
  TEST_RUNS: {
    RANGE_0_30: '0-30 min',
    RANGE_31_60: '31-60 min',
    RANGE_61_120: '61-120 min',
    RANGE_121_240: '121-240 min',
    RANGE_240_PLUS: '240+ min'
  }
}

/**
 * Text Labels for Common Terms
 */
export const TEXT_LABELS = {
  UNKNOWN: 'Unknown',
  UNKNOWN_TEAM: 'Unknown Team',
  UNKNOWN_PROJECT: 'Unknown Project',
  NOT_ESTIMATED: 'Not estimated'
}

/**
 * Project Status Options (for filtering)
 */
export const PROJECT_STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'completed', label: 'Completed' },
  { value: 'on_hold', label: 'On Hold' }
]

/**
 * Report Tab IDs
 */
export const REPORT_TABS = {
  OVERVIEW: 'overview',
  TEAMS: 'teams',
  FEATURES: 'features',
  TEST_CASES: 'testCases',
  TEST_RUNS: 'testRuns',
  BUGS: 'bugs',
  ACCESSIBILITY: 'accessibility'
}

/**
 * Report Section Names (for filtering and exports)
 */
export const REPORT_SECTIONS = {
  TEAMS: 'teams',
  PROJECTS: 'projects',
  FEATURES: 'features',
  TEST_CASES: 'testCases',
  TEST_RUNS: 'testRuns',
  BUGS: 'bugs'
}

/**
 * Date Range Presets
 */
export const DATE_RANGE_PRESETS = {
  TODAY: 'today',
  YESTERDAY: 'yesterday',
  LAST_7_DAYS: 'last_7_days',
  LAST_30_DAYS: 'last_30_days',
  LAST_90_DAYS: 'last_90_days',
  THIS_WEEK: 'this_week',
  THIS_MONTH: 'this_month',
  THIS_QUARTER: 'this_quarter',
  THIS_YEAR: 'this_year',
  LAST_WEEK: 'last_week',
  LAST_MONTH: 'last_month',
  LAST_QUARTER: 'last_quarter',
  LAST_YEAR: 'last_year',
  CUSTOM: 'custom'
}

/**
 * Date Range Preset Labels
 */
export const DATE_RANGE_PRESET_LABELS = {
  [DATE_RANGE_PRESETS.TODAY]: 'Today',
  [DATE_RANGE_PRESETS.YESTERDAY]: 'Yesterday',
  [DATE_RANGE_PRESETS.LAST_7_DAYS]: 'Last 7 Days',
  [DATE_RANGE_PRESETS.LAST_30_DAYS]: 'Last 30 Days',
  [DATE_RANGE_PRESETS.LAST_90_DAYS]: 'Last 90 Days',
  [DATE_RANGE_PRESETS.THIS_WEEK]: 'This Week',
  [DATE_RANGE_PRESETS.THIS_MONTH]: 'This Month',
  [DATE_RANGE_PRESETS.THIS_QUARTER]: 'This Quarter',
  [DATE_RANGE_PRESETS.THIS_YEAR]: 'This Year',
  [DATE_RANGE_PRESETS.LAST_WEEK]: 'Last Week',
  [DATE_RANGE_PRESETS.LAST_MONTH]: 'Last Month',
  [DATE_RANGE_PRESETS.LAST_QUARTER]: 'Last Quarter',
  [DATE_RANGE_PRESETS.LAST_YEAR]: 'Last Year',
  [DATE_RANGE_PRESETS.CUSTOM]: 'Custom Range'
}

/**
 * Chart Types
 */
export const CHART_TYPES = {
  LINE: 'line',
  BAR: 'bar',
  DOUGHNUT: 'doughnut',
  AREA: 'area',
  STACKED_BAR: 'stacked_bar',
  STACKED_AREA: 'stacked_area'
}

/**
 * Export Formats
 */
export const EXPORT_FORMATS = {
  CSV: 'csv',
  XLSX: 'xlsx',
  PDF: 'pdf',
  JSON: 'json'
}

/**
 * Report Aggregation Types
 */
export const AGGREGATION_TYPES = {
  COUNT: 'count',
  SUM: 'sum',
  AVERAGE: 'average',
  MIN: 'min',
  MAX: 'max',
  PERCENTAGE: 'percentage'
}

/**
 * Time Grouping Options
 */
export const TIME_GROUPING = {
  HOUR: 'hour',
  DAY: 'day',
  WEEK: 'week',
  MONTH: 'month',
  QUARTER: 'quarter',
  YEAR: 'year'
}

/**
 * Default Pagination Settings for Reports
 */
export const REPORT_PAGINATION = {
  DEFAULT_PAGE_SIZE: 10,
  PAGE_SIZE_OPTIONS: [10, 20, 50, 100],
  MAX_ITEMS_WITHOUT_PAGINATION: 10
}

/**
 * Table Column Keys (for consistency across reports)
 */
export const TABLE_COLUMN_KEYS = {
  // Common columns
  ID: 'id',
  NAME: 'name',
  TITLE: 'title',
  STATUS: 'status',
  PRIORITY: 'priority',
  CREATED_AT: 'created_at',
  UPDATED_AT: 'updated_at',
  
  // Test Case columns
  TEST_CASE_ID: 'testCaseId',
  TEST_TYPE: 'type',
  CASE_TYPE: 'case_type',
  AUTOMATION_STATUS: 'automation_status',
  
  // Test Run columns
  ENVIRONMENT: 'environment',
  PROGRESS: 'progress',
  
  // Bug columns
  BUG_ID: 'bugId',
  SEVERITY: 'severity',
  TYPE: 'type',
  
  // Feature columns
  FEATURE_ID: 'featureId',
  DEV_ASSIGNEE: 'dev_assignee',
  QA_ASSIGNEE: 'qa_assignee',
  START_DATE: 'start_date',
  DUE_DATE: 'due_date',
  
  // Project/Team columns
  PROJECT_ID: 'project_id',
  TEAM_ID: 'teamId',
  PROJECTS_COUNT: 'projectsCount',
  FEATURES_COUNT: 'featuresCount',
  TEST_CASES_COUNT: 'testCasesCount',
  BUGS_COUNT: 'bugsCount'
}

/**
 * Table Column Configurations
 * Reusable column definitions for report tables
 */
export const TABLE_COLUMN_CONFIGS = {
  // Teams Table
  TEAMS: {
    NAME: { key: 'name', sortable: true, align: 'left' },
    PROJECTS_COUNT: { key: 'projectsCount', sortable: true, align: 'center' },
    FEATURES_COUNT: { key: 'featuresCount', sortable: true, align: 'center' },
    TEST_CASES_COUNT: { key: 'testCasesCount', sortable: true, align: 'center' },
    TEST_RUNS_COUNT: { key: 'testRunsCount', sortable: true, align: 'center' },
    BUGS_COUNT: { key: 'bugsCount', sortable: true, align: 'center' }
  },
  
  // Projects Table
  PROJECTS: {
    NAME: { key: 'name', sortable: true, align: 'left' },
    TEAM: { key: 'teamId', sortable: false, align: 'left' },
    FEATURES_COUNT: { key: 'featuresCount', sortable: true, align: 'center' },
    TEST_CASES_COUNT: { key: 'testCasesCount', sortable: true, align: 'center' },
    TEST_RUNS_COUNT: { key: 'testRunsCount', sortable: true, align: 'center' },
    BUGS_COUNT: { key: 'bugsCount', sortable: true, align: 'center' }
  },
  
  // Features Table
  FEATURES: {
    ID: { key: 'featureId', sortable: true, align: 'left' },
    TITLE: { key: 'title', sortable: true, align: 'left' },
    PROJECT: { key: 'project_id', sortable: false, align: 'left' },
    STATUS: { key: 'status', sortable: true, align: 'left' },
    PRIORITY: { key: 'priority', sortable: true, align: 'left' },
    DEVELOPER: { key: 'dev_assignee', sortable: false, align: 'left' },
    QA: { key: 'qa_assignee', sortable: false, align: 'left' },
    START_DATE: { key: 'start_date', sortable: true, align: 'left' },
    DUE_DATE: { key: 'due_date', sortable: true, align: 'left' }
  },
  
  // Test Cases Table
  TEST_CASES: {
    ID: { key: 'testCaseId', sortable: true, align: 'left' },
    TITLE: { key: 'title', sortable: true, align: 'left' },
    TYPE: { key: 'type', sortable: true, align: 'left' },
    PRIORITY: { key: 'priority', sortable: true, align: 'left' },
    STATUS: { key: 'status', sortable: true, align: 'left' },
    AUTOMATION: { key: 'automation_status', sortable: true, align: 'left' },
    PROJECT: { key: 'project_id', sortable: false, align: 'left' }
  },
  
  // Test Runs Table
  TEST_RUNS: {
    TITLE: { key: 'name', sortable: true, align: 'left' },
    STATUS: { key: 'status', sortable: true, align: 'left' },
    ENVIRONMENT: { key: 'environment', sortable: true, align: 'left' },
    PROGRESS: { key: 'progress', sortable: true, align: 'left' },
    CREATED_BY: { key: 'created_by', sortable: false, align: 'left' },
    PROJECT: { key: 'project_id', sortable: false, align: 'left' },
    CREATED: { key: 'created_at', sortable: true, align: 'left' }
  },
  
  // Bugs Table
  BUGS: {
    ID: { key: 'bugId', sortable: true, align: 'left' },
    TITLE: { key: 'title', sortable: true, align: 'left' },
    TYPE: { key: 'type', sortable: true, align: 'left' },
    STATUS: { key: 'status', sortable: true, align: 'left' },
    SEVERITY: { key: 'severity', sortable: true, align: 'left' },
    PRIORITY: { key: 'priority', sortable: true, align: 'left' },
    ENVIRONMENT: { key: 'environment', sortable: true, align: 'left' },
    ASSIGNED_TO: { key: 'assigned_to', sortable: false, align: 'left' },
    PROJECT: { key: 'project_id', sortable: false, align: 'left' }
  }
}

/**
 * Filter Field Names (for consistent filter keys)
 */
export const FILTER_FIELDS = {
  TEAMS: 'teamIds',
  PROJECTS: 'projectIds',
  STATUS: 'status',
  PRIORITY: 'priority',
  SEVERITY: 'severity',
  TYPE: 'type',
  ENVIRONMENT: 'environment',
  TEST_TYPE: 'testType',
  CASE_TYPE: 'caseType',
  AUTOMATION_STATUS: 'automationStatus',
  DEV_ASSIGNEES: 'devAssignees',
  QA_ASSIGNEES: 'qaAssignees',
  START_DATE_FROM: 'startDateFrom',
  START_DATE_TO: 'startDateTo',
  DUE_DATE_FROM: 'dueDateFrom',
  DUE_DATE_TO: 'dueDateTo',
  SEARCH: 'search',
  INCLUDE_DELETED: 'includeDeleted'
}

/**
 * Chart Color Palettes
 */
export const CHART_COLORS = {
  PRIMARY: [
    '#6366f1', // indigo-500
    '#8b5cf6', // violet-500
    '#d946ef', // fuchsia-500
    '#ec4899', // pink-500
    '#f43f5e', // rose-500
    '#ef4444', // red-500
    '#f97316', // orange-500
    '#f59e0b', // amber-500
    '#eab308', // yellow-500
    '#84cc16', // lime-500
    '#22c55e', // green-500
    '#10b981'  // emerald-500
  ],
  STATUS: {
    OPEN: '#6b7280',      // gray-500
    IN_PROGRESS: '#3b82f6', // blue-500
    IN_REVIEW: '#eab308',   // yellow-500
    TESTING: '#8b5cf6',     // violet-500
    COMPLETED: '#22c55e',   // green-500
    CLOSED: '#10b981',      // emerald-500
    CANCELLED: '#ef4444',   // red-500
    BLOCKED: '#dc2626'      // red-600
  },
  PRIORITY: {
    CRITICAL: '#dc2626', // red-600
    HIGH: '#f97316',     // orange-500
    MEDIUM: '#eab308',   // yellow-500
    LOW: '#6b7280'       // gray-500
  },
  SEVERITY: {
    CRITICAL: '#dc2626', // red-600
    HIGH: '#f97316',     // orange-500
    MEDIUM: '#eab308',   // yellow-500
    LOW: '#10b981'       // emerald-500
  }
}

/**
 * Empty State Messages (fallbacks if i18n not loaded)
 */
export const EMPTY_MESSAGES = {
  NO_DATA: 'No data available',
  NO_FILTERED_RESULTS: 'No results match your filters',
  NO_TEAMS: 'No teams found',
  NO_PROJECTS: 'No projects found',
  NO_FEATURES: 'No features found',
  NO_TEST_CASES: 'No test cases found',
  NO_TEST_RUNS: 'No test runs found',
  NO_BUGS: 'No bugs found'
}

/**
 * Loading Messages (fallbacks if i18n not loaded)
 */
export const LOADING_MESSAGES = {
  DEFAULT: 'Loading...',
  ANALYTICS: 'Loading analytics data...',
  CHARTS: 'Loading charts...',
  TABLES: 'Loading tables...',
  EXPORT: 'Exporting data...'
}

/**
 * Report Stats Labels
 */
export const STATS_LABELS = {
  TOTAL: 'Total',
  ACTIVE: 'Active',
  INACTIVE: 'Inactive',
  COMPLETED: 'Completed',
  OPEN: 'Open',
  CLOSED: 'Closed',
  IN_PROGRESS: 'In Progress',
  PENDING: 'Pending',
  AUTOMATED: 'Automated',
  MANUAL: 'Manual',
  CRITICAL: 'Critical',
  HIGH: 'High',
  MEDIUM: 'Medium',
  LOW: 'Low'
}

/**
 * Export Report Sheet Names
 */
export const EXPORT_SHEET_NAMES = {
  TEAMS: 'Teams',
  PROJECTS: 'Projects',
  FEATURES: 'Features',
  TEST_CASES: 'Test Cases',
  TEST_RUNS: 'Test Runs',
  BUGS: 'Bugs',
  ACCESSIBILITY: 'Accessibility'
}

/**
 * Export Report Column Headers
 */
export const EXPORT_HEADERS = {
  TEAMS: [
    'Team Name',
    'Total Projects',
    'Total Features',
    'Total Test Cases',
    'Total Bugs',
    'Created Date'
  ],
  PROJECTS: [
    'Project Name',
    'Team',
    'Features',
    'Test Cases',
    'Test Runs',
    'Bugs',
    'Created Date'
  ],
  FEATURES: [
    'Feature ID',
    'Title',
    'Project',
    'Status',
    'Priority',
    'Developer',
    'QA',
    'Start Date',
    'Due Date',
    'Created Date'
  ],
  TEST_CASES: [
    'Test Case ID',
    'Title',
    'Project',
    'Type',
    'Case Type',
    'Priority',
    'Status',
    'Automation Status',
    'Created Date'
  ],
  TEST_RUNS: [
    'Test Run ID',
    'Title',
    'Project',
    'Status',
    'Environment',
    'Progress',
    'Created Date'
  ],
  BUGS: [
    'Bug ID',
    'Title',
    'Project',
    'Type',
    'Status',
    'Severity',
    'Priority',
    'Environment',
    'Created Date'
  ],
  ACCESSIBILITY: [
    'Test Name',
    'Page URL',
    'Issue ID',
    'Impact',
    'Description',
    'Help URL',
    'Node',
    'Status',
    'Assigned To',
    'First Detected',
    'Last Detected',
    'Report Dates'
  ]
}

/**
 * Helper Functions for Badge Display
 * Note: Badge color constants are imported from statusColors.js and uiColors.js
 */

/**
 * Format text for display (capitalize first letter, replace underscores/hyphens)
 * @param {string} text - Text to format
 * @returns {string} Formatted text
 */
export const formatDisplayText = (text) => {
  if (!text) return ''
  return text
    .replace(/_/g, ' ')
    .replace(/-/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

/**
 * Format automation status text
 * @param {string} status - Automation status
 * @returns {string} Formatted text
 */
export const formatAutomationStatus = (status) => {
  if (!status || status === 'manual' || status === 'not_automated') {
    return 'Manual'
  }
  return formatDisplayText(status)
}

/**
 * Get Badge component-compatible color name from status
 * These functions map status values to Badge component color props
 * The actual color definitions come from uiColors.js BADGE_COLORS
 */

/**
 * Get badge color for priority
 * Uses STATUS_COLORS from statusColors.js for consistency
 * @param {string} priority - Priority value
 * @returns {string} Badge color name
 */
export const getPriorityColor = (priority) => {
  const priorityMap = {
    critical: STATUS_COLORS.CRITICAL,
    high: STATUS_COLORS.HIGH,
    medium: STATUS_COLORS.MEDIUM,
    low: STATUS_COLORS.LOW
  }
  
  const priorityColor = priorityMap[priority?.toLowerCase()] || STATUS_COLORS.MEDIUM
  
  // Extract simple color name from badge classes
  const badgeClasses = priorityColor.badge || 'bg-gray-100'
  const colorMatch = badgeClasses.match(/bg-(\w+)-/)
  return colorMatch ? colorMatch[1] : 'gray'
}

/**
 * Get badge color for feature status
 * Uses STATUS_COLORS from statusColors.js for consistency
 * @param {string} status - Status value
 * @returns {string} Badge color name
 */
export const getFeatureStatusColor = (status) => {
  const statusMap = {
    open: STATUS_COLORS.OPEN,
    'in-progress': STATUS_COLORS.IN_PROGRESS,
    'in-review': STATUS_COLORS.IN_REVIEW,
    testing: STATUS_COLORS.TESTING,
    closed: STATUS_COLORS.COMPLETED
  }
  
  const statusColor = statusMap[status?.toLowerCase()] || STATUS_COLORS.DRAFT
  
  // Extract simple color name from badge classes
  // statusColor.badge format: 'bg-{color}-100 text-{color}-800...'
  const badgeClasses = statusColor.badge || 'bg-gray-100'
  const colorMatch = badgeClasses.match(/bg-(\w+)-/)
  return colorMatch ? colorMatch[1] : 'gray'
}

/**
 * Get badge color for test case status
 * @param {string} status - Status value
 * @returns {string} Badge color name
 */
export const getTestCaseStatusColor = (status) => {
  const statusMap = {
    approved: STATUS_COLORS.COMPLETED,
    active: STATUS_COLORS.ACTIVE,
    draft: STATUS_COLORS.DRAFT,
    review: STATUS_COLORS.IN_REVIEW,
    deprecated: STATUS_COLORS.DEPRECATED
  }
  
  const statusColor = statusMap[status?.toLowerCase()] || STATUS_COLORS.DRAFT
  
  // Extract simple color name from badge classes
  const badgeClasses = statusColor.badge || 'bg-gray-100'
  const colorMatch = badgeClasses.match(/bg-(\w+)-/)
  return colorMatch ? colorMatch[1] : 'gray'
}

/**
 * Get badge color for automation status
 * @param {string} status - Automation status value
 * @returns {string} Badge color name
 */
export const getAutomationStatusColor = (status) => {
  const automationMap = {
    automated: STATUS_COLORS.AUTOMATED,
    'semi-automated': STATUS_COLORS.SEMI_AUTOMATED,
    'semi_automated': STATUS_COLORS.SEMI_AUTOMATED,
    in_progress: STATUS_COLORS.IN_PROGRESS,
    manual: STATUS_COLORS.MANUAL,
    not_automated: STATUS_COLORS.MANUAL
  }
  
  const statusColor = automationMap[status?.toLowerCase()] || STATUS_COLORS.MANUAL
  
  // Extract simple color name from badge classes
  const badgeClasses = statusColor.badge || 'bg-gray-100'
  const colorMatch = badgeClasses.match(/bg-(\w+)-/)
  return colorMatch ? colorMatch[1] : 'gray'
}

/**
 * Get badge color for test run status
 * @param {string} status - Test run status value
 * @returns {string} Badge color name
 */
export const getTestRunStatusColor = (status) => {
  const statusMap = {
    COMPLETED: STATUS_COLORS.COMPLETED,
    IN_PROGRESS: STATUS_COLORS.IN_PROGRESS,
    YET_TO_START: STATUS_COLORS.YET_TO_START,
    PAUSED: STATUS_COLORS.PAUSED,
    CANCELLED: STATUS_COLORS.CANCELLED
  }
  
  const statusColor = statusMap[status?.toUpperCase()] || STATUS_COLORS.DRAFT
  
  // Extract simple color name from badge classes
  const badgeClasses = statusColor.badge || 'bg-gray-100'
  const colorMatch = badgeClasses.match(/bg-(\w+)-/)
  return colorMatch ? colorMatch[1] : 'gray'
}

export const getEnvironmentColor = (environment) => {
  const envMap = {
    development: 'blue',
    versioned_staging: 'purple',
    default_staging: 'indigo',
    interop: 'cyan',
    beta: 'orange',
    production: 'red'
  }
  
  return envMap[environment?.toLowerCase()] || 'gray'
}

/**
 * Get badge color for bug status
 * @param {string} status - Bug status value
 * @returns {string} Badge color name
 */
export const getBugStatusColor = (status) => {
  const statusMap = {
    open: STATUS_COLORS.OPEN,
    in_progress: STATUS_COLORS.IN_PROGRESS,
    resolved: STATUS_COLORS.RESOLVED,
    closed: STATUS_COLORS.COMPLETED,
    reopened: STATUS_COLORS.REOPENED,
    blocked: STATUS_COLORS.BLOCKED
  }
  
  const statusColor = statusMap[status?.toLowerCase()] || STATUS_COLORS.DRAFT
  
  // Extract simple color name from badge classes
  const badgeClasses = statusColor.badge || 'bg-gray-100'
  const colorMatch = badgeClasses.match(/bg-(\w+)-/)
  return colorMatch ? colorMatch[1] : 'gray'
}

/**
 * Get badge color for severity
 * @param {string} severity - Severity value
 * @returns {string} Badge color name
 */
export const getSeverityColor = (severity) => {
  // Updated to use new severity values: minor, moderate, major, critical
  const severityMap = {
    critical: STATUS_COLORS.CRITICAL,
    major: STATUS_COLORS.HIGH,      // Major maps to HIGH (orange)
    moderate: STATUS_COLORS.MEDIUM, // Moderate maps to MEDIUM (yellow) - Note: bugHelpers uses blue, but STATUS_COLORS.MEDIUM is yellow
    minor: STATUS_COLORS.LOW        // Minor maps to LOW (green)
  }
  
  const severityColor = severityMap[severity?.toLowerCase()] || STATUS_COLORS.MEDIUM
  
  // Extract simple color name from badge classes
  const badgeClasses = severityColor.badge || 'bg-gray-100'
  const colorMatch = badgeClasses.match(/bg-(\w+)-/)
  return colorMatch ? colorMatch[1] : 'gray'
}

/**
 * Get badge color for test case type
 * @param {string} type - Test case type value
 * @returns {string} Badge color name
 */
export const getTestCaseTypeColor = (type) => {
  const typeMap = {
    functional: STATUS_COLORS.FUNCTIONAL,
    'non-functional': STATUS_COLORS.NON_FUNCTIONAL,
    'non_functional': STATUS_COLORS.NON_FUNCTIONAL,
    api: STATUS_COLORS.API,
    ui: STATUS_COLORS.UI,
    performance: STATUS_COLORS.PERFORMANCE,
    security: STATUS_COLORS.SECURITY
  }
  
  const typeColor = typeMap[type?.toLowerCase()] || STATUS_COLORS.FUNCTIONAL
  
  // Extract simple color name from badge classes
  const badgeClasses = typeColor.badge || 'bg-gray-100'
  const colorMatch = badgeClasses.match(/bg-(\w+)-/)
  return colorMatch ? colorMatch[1] : 'blue'
}

/**
 * Get badge color for bug type
 * @param {string} type - Bug type value
 * @returns {string} Badge color name
 */
export const getBugTypeColor = (type) => {
  const typeMap = {
    bug: STATUS_COLORS.BUG,
    feature: STATUS_COLORS.FEATURE,
    enhancement: STATUS_COLORS.ENHANCEMENT,
    task: STATUS_COLORS.TASK
  }
  
  const typeColor = typeMap[type?.toLowerCase()] || STATUS_COLORS.BUG
  
  // Extract simple color name from badge classes
  const badgeClasses = typeColor.badge || 'bg-gray-100'
  const colorMatch = badgeClasses.match(/bg-(\w+)-/)
  return colorMatch ? colorMatch[1] : 'purple'
}

